import axios from 'axios';
import { getAccessToken, isSessionValid, setSession, clearSession } from './authStorage';

const baseURL = import.meta.env.VITE_API_URL;

export const apiPublic = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

export const apiPrivate = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

function forceLogout(reason) {
  clearSession();
  if (window.location.pathname !== '/login') {
    window.location.assign(reason ? `/login?reason=${reason}` : '/login');
  }
}

apiPrivate.interceptors.request.use(async (config) => {
  if (!isSessionValid()) {
    try {
      const resp = await apiPublic.post(
        '/api/refresh',
        {},
        { headers: { Authorization: `Bearer ${getAccessToken()}` } }
      );
      setSession(resp.data);
    } catch (e) {
      forceLogout();
      throw e;
    }
  }

  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  return config;
});

let isRefreshing = false;
let queue = [];

function resolveQueue(err, token) {
  queue.forEach(({ resolve, reject }) => {
    if (err) reject(err);
    else resolve(token);
  });
  queue = [];
}

apiPrivate.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config;
    if (!error.response) throw error;

    const status = error.response.status;
    const url = original?.url || '';

    if (status === 402 && error.response.data?.code === 'TENANT_INACTIVE') {
      forceLogout('tenant_inactive');
      throw error;
    }

    if (
      status !== 401 ||
      original._retry ||
      url.includes('/api/login') ||
      url.includes('/api/refresh')
    ) {
      throw error;
    }

    original._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        queue.push({
          resolve: (token) => {
            original.headers.Authorization = `Bearer ${token}`;
            resolve(apiPrivate(original));
          },
          reject
        });
      });
    }

    isRefreshing = true;

    try {
      const resp = await apiPublic.post(
        '/api/refresh',
        {},
        { headers: { Authorization: `Bearer ${getAccessToken()}` } }
      );
      setSession(resp.data);

      const token = getAccessToken();
      resolveQueue(null, token);

      original.headers.Authorization = `Bearer ${token}`;
      return apiPrivate(original);
    } catch (e) {
      resolveQueue(e, null);
      forceLogout();
      throw e;
    } finally {
      isRefreshing = false;
    }
  }
);
